import * as React from "react";
import {useIntl} from "react-intl";
import KpiCard from "../KpiCard";
import {changeBetween} from "../../../helper/analysisChange";
import {monthToDate} from "../../../helper/analysisMonths";

type MonthKpiCardsProps = {
    totalLabel: string // e.g. "Income"
    selectedMonth: string // YYYY-MM
    total: number
    previousTotal: number | null // null if the range does not contain the month before
    averageTotals: number[] // totals of the months the average is built from
    formatAmount: (amount: number) => string
    increaseIsGood: boolean // colors an increase green for revenue and red for costs
    children: React.ReactNode // the fourth card, specific to the view
};

/**
 * The cards above the chart: total of the selected month, change to the month before and the
 * average of the months of the range. The view adds a fourth card of its own.
 */
const MonthKpiCards: React.FC<MonthKpiCardsProps> = ({totalLabel, selectedMonth, total, previousTotal, averageTotals, formatAmount, increaseIsGood, children}) => {
    const intl = useIntl();
    const monthLabel = intl.formatDate(monthToDate(selectedMonth), {month: 'long', year: 'numeric', timeZone: 'UTC'});
    const average = averageTotals.length > 0 ? Math.round(averageTotals.reduce((sum, amount) => sum + amount, 0) / averageTotals.length) : 0;

    const change = previousTotal === null ? null : changeBetween(total, previousTotal);
    const isIncrease = change !== null && change.delta > 0;
    const trend = change === null || change.delta === 0 ? 'neutral' : (isIncrease === increaseIsGood ? 'up' : 'down');
    const percentLabel = change?.percent === null || change === null
        ? '—'
        : intl.formatNumber(change.percent, {style: 'percent', maximumFractionDigits: 1, signDisplay: 'exceptZero'});

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label={totalLabel} value={formatAmount(total)} sub={monthLabel}/>
            <KpiCard
                label={intl.formatMessage({id: 'component.analysis.kpi.change'})}
                value={change === null ? '—' : (change.delta > 0 ? '+' : '') + formatAmount(change.delta)}
                sub={intl.formatMessage({id: 'component.analysis.kpi.change.sub'}, {percent: percentLabel})}
                trend={trend}
            />
            <KpiCard
                label={intl.formatMessage({id: 'component.analysis.kpi.average'})}
                value={formatAmount(average)}
                sub={intl.formatMessage({id: 'component.analysis.kpi.average.sub'}, {count: averageTotals.length})}
            />
            {children}
        </div>
    );
};

export default MonthKpiCards;
